import uuid from 'react-uuid'

import { gql } from '@apollo/client'
import { getApolloClient } from '../services/apolloClient'

import { GetDataSystems, GetDataSystem } from '../graphql/DataSystems'

import { GetDataTypes, GetDataType } from '../graphql/DataTypes'

import { GetDeliveryConfig } from '../graphql/DeliveryConfig'

import { GetIntegrations, GetIntegration } from '../graphql/Integrations'

import { GetHolds } from '../graphql/Holds'

import {
  AddMaintenance,
  GetMaintenances,
  DeleleteMaintenance,
  GetMaintenanceTemplate,
  GetMaintenanceTemplates,
  AddMaintenanceTemplate,
  DeleleteMaintenanceTemplate,
} from '../graphql/MaintenanceTemplates'

import { GetJobs, GetJob, RetryJob, FailJob, GetJobPayloadPresignedURL, GetPendingBatchJobs } from '../graphql/Jobs'

import { GetDataSource, GetDataSources } from '../graphql/DataSource'

import { ListManualUploads, SendToManualGateway } from '../graphql/ManualGateway'

import { ComponentTypes } from '../graphql/ComponentTypes'

import { GetSFTPGatewayLog, GetSFTPGatewayLogs } from '../graphql/SFTPGatewayLogs'

import { GetFTPGatewayLog, GetFTPGatewayLogs } from '../graphql/FTPGatewayLogs'

import { GetFailureConfig, SetFailureConfig } from '../graphql/FailureConfig'

import { GetEndpoint, GetEndpoints } from '../graphql/Endpoint'

import { GetGatewaySchedulerLog, GetGatewaySchedulerLogs } from '../graphql/GatewaySchedulerLog'

import { GetBatchDeliveryManagerLog, GetBatchDeliveryManagerLogs } from '../graphql/BatchDeliveryManagerLog'

import { GetMetadataKeys } from '../graphql/Metadata'

import { CurrentUser } from '../graphql/Auth'

import { GetUsers, GetUser, AddUser, UpdateUser, DeleteUser } from '../graphql/Users'
import { mockQuery, mockMutation } from '../mocks/graphqlMocks'

function toDocument(document) {
  return typeof document === 'string' ? gql(document) : document
}

function getPrimaryField(document) {
  const operation = document.definitions && document.definitions.find((def) => def.kind === 'OperationDefinition')
  if (!operation || !operation.selectionSet || operation.selectionSet.selections.length === 0) {
    return null
  }

  const selection = operation.selectionSet.selections[0]
  return selection && selection.name && selection.name.value ? selection.name.value : null
}

function stripTypename(value) {
  if (!value || typeof value !== 'object') {
    return value
  }

  if (Array.isArray(value)) {
    return value.map(stripTypename)
  }

  return Object.keys(value).reduce((acc, key) => {
    if (key === '__typename') {
      return acc
    }

    acc[key] = stripTypename(value[key])
    return acc
  }, {})
}

async function executeQuery(document, variables = {}, options = {}) {
  const client = getApolloClient()

  const doc = toDocument(document)

  if (window.config && window.config.useMockBackend) {
    const fieldName = getPrimaryField(doc)
    return mockQuery(fieldName, variables)
  }

  return client.query({
    query: doc,
    variables,
    fetchPolicy: options.fetchPolicy || 'network-only',
  })
}

async function executeMutation(document, variables = {}) {
  const client = getApolloClient()

  const doc = toDocument(document)

  if (window.config && window.config.useMockBackend) {
    const fieldName = getPrimaryField(doc)
    return mockMutation(fieldName, variables)
  }

  return client.mutate({
    mutation: doc,
    variables,
  })
}

async function listManualUploads() {
  const result = await executeQuery(ListManualUploads)
  return result
    .data
    .manualPayloads
    .map(({ payloadUrl }) => payloadUrl)
}

async function sendToManualGateway({ dataSourceId, payloadUrl }) {
  const result = await executeMutation(SendToManualGateway, { dataSourceId, payloadUrl })
  return result
}

async function loadDataSystems({ count, startPosition, filterConfig }) {
  const result = await executeQuery(GetDataSystems, { count, cursor: startPosition, filterConfig })
  return result.data.dataSystems
}

async function loadDataSystem({ id, deliveryConfigCount, deliveryConfigCursor }) {
  const result = await executeQuery(GetDataSystem, { id, deliveryConfigCount, deliveryConfigCursor })
  return result
    .data
    .dataSystem
}

async function loadGatewaySchedulerLog({ id }) {
  const result = await executeQuery(GetGatewaySchedulerLog, { id })
  return result
    .data
    .gatewaySchedulerLog  
}

async function loadBatchDeliveryManagerLog({ id }) {
  const result = await executeQuery(GetBatchDeliveryManagerLog, { id })
  return result
    .data
    .batchDeliveryManagerLog
}

async function loadJob({ id, logCount, logCursor }) {
  const result = await executeQuery(GetJob, { id, logCount, logCursor, asc: true })
  return result
    .data
    .job
}

async function getJobPayloadPresignedUrl({ jobId, index }) {
  const result = await executeQuery(GetJobPayloadPresignedURL, { id: jobId, index })
  return result.data.job.presignedPayloadUrl
}

async function getPendingBatchJobs() {
  const result = await executeQuery(GetPendingBatchJobs, {})
  return result.data.pendingBatchJobs
}

async function loadMetadataKeys({ count, startPosition, filterConfig }) {
  const result = await executeQuery(GetMetadataKeys, { count, cursor: startPosition, filterConfig })
  return result.data.metadataKeys
}

async function loadDataTypes({ count, startPosition, filterConfig }) {
  const result = await executeQuery(GetDataTypes, { count, cursor: startPosition, filterConfig })
  return result.data.dataTypes
}

async function loadDataSources({ count, startPosition, filterConfig }) {
  const result = await executeQuery(GetDataSources, { count, cursor: startPosition, filterConfig })
  return result.data.dataSources  
}

async function addMaintenance({ template, startTime, endTime }) {
  const result = await executeMutation(AddMaintenance, { id: uuid(), template, startTime, endTime })
  return result.data.addSystemMaintenance
}

async function loadMaintenances({ count, startPosition }) {
  const result = await executeQuery(GetMaintenances, { count, cursor: startPosition })
  return result.data.systemMaintenances
}

async function loadMaintenanceTemplates({ count, startPosition }) {
  const result = await executeQuery(GetMaintenanceTemplates, { count, cursor: startPosition })
  return result.data.systemMaintenanceTemplates
}

async function loadHolds({ count, startPosition }) {
  const result = await executeQuery(GetHolds, { count, cursor: startPosition })
  return result.data.adHocHolds
}

async function loadIntegrations({ count, startPosition, filterConfig }) {
  const result = await executeQuery(GetIntegrations, { count, cursor: startPosition, filterConfig })
  return result.data.integrations
}

async function loadJobs({ count, startPosition, filterConfig, user }) {
  const isAdmin = !!(user && user.role === 'admin')
  
  const filterConf = filterConfig
    ? { ...filterConfig, showHidden: isAdmin }
    : { showHidden: isAdmin }

  const result = await executeQuery(GetJobs, { count, cursor: startPosition, filterConfig: filterConf })

  return result.data.jobs
}

async function loadEndpoint({ id }) {
  const result = await executeQuery(GetEndpoint, { id })
  const endpoint = result.data.endpoint
  if (!endpoint) {
    return endpoint
  }

  return {
    ...endpoint,
    config: stripTypename(endpoint.config),
  }
}

async function loadEndpoints({ count, startPosition }) {
  const result = await executeQuery(GetEndpoints, { count, cursor: startPosition })
  const endpoints = result.data.endpoints
  if (!endpoints || !endpoints.edges) {
    return endpoints
  }

  return {
    ...endpoints,
    edges: endpoints.edges.map((edge) => {
      if (!edge || !edge.node) {
        return edge
      }

      return {
        ...edge,
        node: {
          ...edge.node,
          config: stripTypename(edge.node.config),
        },
      }
    }),
  }
}

async function loadGatewaySchedulerLogs({ count, startPosition, filterConfig }) {
  const { regionFilters = [window.config.region], createdTimeFilter = {} } = filterConfig || {}
  const newFilterConfig = { regionFilters, createdTimeFilter }

  const result = await executeQuery(GetGatewaySchedulerLogs, { count, cursor: startPosition, filterConfig: newFilterConfig })
  return result.data.gatewaySchedulerLogs
}

async function loadBatchDeliveryManagerLogs({ count, startPosition, filterConfig }) {
  const { regionFilters = [window.config.region], createdTimeFilter = {} } = filterConfig || {}
  const newFilterConfig = { regionFilters, createdTimeFilter }

  const result = await executeQuery(GetBatchDeliveryManagerLogs, { count, cursor: startPosition, filterConfig: newFilterConfig })
  return result.data.batchDeliveryManagerLogs
}

async function loadUsers({ count, startPosition }) {
  const result = await executeQuery(GetUsers, { count, cursor: startPosition })
  return result.data.users
}

async function loadUser({ id }) {
  const result = await executeQuery(GetUser, { id })
  return result.data.user
}

async function addUser({ email, role }) {
  const result = await executeMutation(AddUser, { email, role })
  return result.data.addUser
}

async function updateUser({ email, role }) {
  const result = await executeMutation(UpdateUser, { email, role })
  return result.data.updateUser
}

async function deleteUser({ id }) { 
  const result = await executeMutation(DeleteUser, { id })
  return result.data.deleteUser
}

async function retryJob({ jobId, forceRetry }) {
  const result = await executeMutation(RetryJob, { id: jobId, forceRetry })
  return result.data.retryJob
}

async function failJob({ jobId }) {
  const result = await executeMutation(FailJob, { id: jobId })
  return result.data.failJob
}

async function loadMaitenanceTemplate({ templateId }) {
  const result = await executeQuery(GetMaintenanceTemplate, { id: templateId })
  return result.data.systemMaintenanceTemplate
}

async function loadIntegration(id) {
  const result = await executeQuery(GetIntegration, { id })
  return result.data.integration
}

async function loadDataType(id) {
  const result = await executeQuery(GetDataType, { id })
  return result.data.dataType
}

async function loadDeliveryConfig(id) {
  const result = await executeQuery(GetDeliveryConfig, { id })
  return result.data.deliveryConfig
}

async function loadDataSource(id) {
  const result = await executeQuery(GetDataSource, { id })
  const dataSource = result.data && result.data.dataSource

  if (!dataSource) {
    return dataSource
  }

  // Clone so we can enrich with gateway logs even when GraphQL response objects are frozen.
  const enrichedDataSource = { ...dataSource }

  if (dataSource.type === "SFTP") {
    const logResult = await executeQuery(GetSFTPGatewayLogs, { filterConfig: { dataSource: dataSource.id } })
    if (logResult.data && logResult.data.sftpGatewayLogs) {
      enrichedDataSource.sftpGatewayLogs = logResult.data.sftpGatewayLogs
    }
  }
  else if (dataSource.type === "FTP") {
    const logResult = await executeQuery(GetFTPGatewayLogs, { filterConfig: { dataSource: dataSource.id } })
    if (logResult.data && logResult.data.ftpGatewayLogs) {
      enrichedDataSource.ftpGatewayLogs = logResult.data.ftpGatewayLogs
    }
  }  

  return enrichedDataSource
}

async function loadSFTPGatewayLogs({ count, cursor, filterConfig }) {
  const results = await executeQuery(GetSFTPGatewayLogs, { count, cursor, filterConfig })
  return results.data.sftpGatewayLogs
}

async function loadFTPGatewayLogs({ count, cursor, filterConfig }) {
  const results = await executeQuery(GetFTPGatewayLogs, { count, cursor, filterConfig })
  return results.data.ftpGatewayLogs
}

async function loadSFTPGatewayLog(id) {
  let results = await executeQuery(GetSFTPGatewayLog, { id })
  let sftpGatewayLog = results.data.sftpGatewayLog
  let ftpGatewayLog
  if (sftpGatewayLog && sftpGatewayLog.logURL) {
    
    try {
      const response = await fetch(sftpGatewayLog.logURL);
      const logData = await response.json()

      sftpGatewayLog = {...sftpGatewayLog, ...logData }
    } catch (err) {
      const errorLogData = {
        devLog: {},
        businessLog: [
          {
            message: `Log could not be retreived from ${sftpGatewayLog.logURL}`,
            type: "Error",
            timestamp: new Date().toISOString()
          },
          {
            message: err && err.message,
            type: "Error",
            timestamp: new Date().toISOString()
          }
        ]
      }
      sftpGatewayLog = { ...sftpGatewayLog, ...errorLogData }
    }
    return sftpGatewayLog
  }

  results = await executeQuery(GetFTPGatewayLog, { id })
  ftpGatewayLog = results.data.ftpGatewayLog

  if (ftpGatewayLog && ftpGatewayLog.logURL) {
    
    try {
      const response = await fetch(ftpGatewayLog.logURL);
      const logData = await response.json()

      ftpGatewayLog = {...ftpGatewayLog, ...logData }
    } catch (err) {
      const errorLogData = {
        devLog: {},
        businessLog: [
          {
            message: `Log could not be retreived from ${ftpGatewayLog.logURL}`,
            type: "Error",
            timestamp: new Date().toISOString()
          },
          {
            message: err && err.message,
            type: "Error",
            timestamp: new Date().toISOString()
          }
        ]
      }
      ftpGatewayLog = { ...ftpGatewayLog, ...errorLogData }
    }
    return ftpGatewayLog
  }

  return null
  
}

async function loadComponentTypes() {
  const result = await executeQuery(ComponentTypes)
  return result
    .data
    .componentTypes
}

async function loadFailureConfig() {
  const result = await executeQuery(GetFailureConfig)
  return result
    .data
    .failureConfig
}

async function saveFailureConfig(config) {
  const result = await executeMutation(SetFailureConfig(config))
  return result
    .data
    .failureConfig
}

async function getUser() {
  const result = await executeQuery(CurrentUser)
  return result
    .data
    .currentUser
}

async function addMaintenanceTemplate({ template }) {
  const result = await executeMutation(AddMaintenanceTemplate(template))
  return result
    .data
    .addSystemMaintenanceTemplate
}

async function deleteMaintenanceTemplate({ templateId }) {
  const result = await executeMutation(DeleleteMaintenanceTemplate, { id: templateId })
  return result.data.deleteSystemMaintenanceTemplate
}

async function deleteMaintenance({ id }) {
  console.log('...deleteMaintenance', id)
  const result = await executeMutation(DeleleteMaintenance, { id })
  console.log('...result', result)
  return result.data.deleteSystemMaintenance
}

async function loadData({ category, count, startPosition, filterCriteria, user }) {
  const filterConfig = filterCriteria && filterCriteria[category]

  if (category === 'dataSystems') {
    return await loadDataSystems({ count, startPosition, filterConfig })
  } else if (category === 'dataTypes') {
    return await loadDataTypes({ count, startPosition, filterConfig })
  } else if (category === 'integrations') {
    return await loadIntegrations({ count, startPosition, filterConfig })
  } else if (category === 'dataSources') {
    return await loadDataSources({ count, startPosition, filterConfig })
  } else if (category === 'maintenance') {
    return await loadMaintenances({ count, startPosition })
  } else if (category === 'maintenanceTemplates') {
    return await loadMaintenanceTemplates({ count, startPosition })
  } else if (category === 'holds') {
    return await loadHolds({ count, startPosition })
  } else if (category === 'jobs')  {
    return await loadJobs({ count, startPosition, filterConfig, user })
  } else if (category === 'endpoints') {
    return await loadEndpoints({ count, startPosition })
  } else if (category === 'gatewaySchedulerLogs') {
    return await loadGatewaySchedulerLogs({ count, startPosition, filterConfig })
  } else if (category === 'batchDeliveryManagerLogs') {
    return await loadBatchDeliveryManagerLogs({ count, startPosition, filterConfig })
  } else if (category === 'users') {
    return await loadUsers({ count, startPosition })
  } else {
    return []
  }
}

async function loadDataItem(category, id) {
  if (category === 'dataSystems') {
    return await loadDataSystem({ id })
  } else if (category === 'dataTypes') {
    return await loadDataType(id)
  } else if (category === 'jobs') {
    return await loadJob({ id })
  } else {
    return null
  }
}

export default {
  listManualUploads,
  sendToManualGateway,
  loadDeliveryConfig,  
  loadIntegrations,
  loadJobs,
  retryJob,
  failJob,
  loadDataSources,
  loadDataSource,
  loadJob,
  loadIntegration,
  loadDataSystems,  
  loadDataSystem,
  loadDataTypes,
  loadDataItem,
  loadData,
  loadComponentTypes,
  loadFailureConfig,
  saveFailureConfig,
  loadEndpoint,  
  loadGatewaySchedulerLog,
  loadBatchDeliveryManagerLog,
  loadSFTPGatewayLog,
  loadSFTPGatewayLogs,
  loadFTPGatewayLogs,
  getUser,
  loadUser,
  addUser,
  deleteUser,
  updateUser,
  getJobPayloadPresignedUrl,
  getPendingBatchJobs,
  loadMetadataKeys,
  addMaintenance,
  addMaintenanceTemplate,
  deleteMaintenanceTemplate,
  deleteMaintenance,
  loadMaitenanceTemplate,
}
